import Typography from '@material-ui/core/Typography'
import { permissionsSelectors } from 'ducks/permissions'
import { Grid, Skeleton, styled } from 'opensolar-ui'
import { ComponentPlaceHolderImage } from 'pages/ordering/catalogv3/ProductTile'
import useIsPriceOnApplication from 'pages/ordering/hooks/useIsPriceOnApplication'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { BulkOptionType, DistributorDataTypeV2 } from '../../../elements/hardwareSelectorV2/types'
import PriceDetail, { PriceDetailLayout } from '../detail/content/price/PriceDetail'

export const Wrapper = styled('div')({
  display: 'flex',
  justifyContent: 'space-evenly',
  backgroundColor: 'white',
  padding: 20,
  borderRadius: 8,
})

const ImageContainer = styled(Grid)({
  width: '202px',
  height: '302px',
  flexShrink: 0,
})

const DescriptionContainer = styled(Grid)({
  height: 'fit-content',
})

const ImageWrapper = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexWrap: 'wrap',
})

const ProductImage = styled('img')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '302px',
  objectFit: 'contain',
  borderRadius: 8,
})

const LogoImage = styled('img')<{ hide: boolean }>(({ hide }) => ({
  height: 40,
  width: 80,
  objectFit: 'contain',
  display: hide ? 'none' : 'block',
}))

const ProductCode = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutralGrey[900],
  flexDirection: 'column',
  gap: 8,
  fontSize: 16,
}))

const ProductCard = ({
  hasSelectedDistributor,
  shouldShowSelectedDistributorPricing,
  displayDetailsDistributor,
  manufacturerUrl,
  manufacturerName,
  outputPower,
  currencySymbol,
  loading,
  bulkOption,
  dealPrice,
  effectiveDiscountFraction,
  pricePerWatt,
  dealBadge,
}: {
  hasSelectedDistributor: boolean
  shouldShowSelectedDistributorPricing: boolean
  displayDetailsDistributor?: DistributorDataTypeV2
  manufacturerUrl?: string
  manufacturerName?: string
  outputPower: number
  currencySymbol: string
  loading: boolean
  bulkOption: BulkOptionType | null
  dealPrice?: number
  effectiveDiscountFraction?: number
  pricePerWatt?: string | null
  dealBadge?: React.ReactNode
}) => {
  const imageUrl = displayDetailsDistributor?.image_url
  const shortDescription = displayDetailsDistributor?.short_description
  const code = displayDetailsDistributor?.code
  const tradePrice = displayDetailsDistributor?.price_detail.price_ex_vat
  const tradeDiscountValue = displayDetailsDistributor?.price_detail.discount_value

  const price = dealPrice ?? tradePrice
  const discountPercentValue = effectiveDiscountFraction ?? tradeDiscountValue
  const { allowView: allowViewPricing } = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'component_pricing_costing')
  )

  const isPriceOnApplication = useIsPriceOnApplication(displayDetailsDistributor?.distributor as any)
  const [manufacturerUrlSrc, setManufacturerUrlSource] = useState<string | undefined>(manufacturerUrl)
  const [productImage, setProductImage] = useState<string | undefined>(imageUrl)

  useEffect(() => {
    setProductImage(imageUrl)
  }, [imageUrl])

  useEffect(() => {
    setManufacturerUrlSource(manufacturerUrl)
  }, [manufacturerUrl])

  return (
    <ProductCardLayout
      Thumb={
        productImage ? (
          <ProductImage src={productImage} alt="Product" onError={() => setProductImage(undefined)} />
        ) : (
          <ComponentPlaceHolderImage />
        )
      }
      ManufacturerLogo={
        manufacturerUrlSrc ? (
          <LogoImage src={manufacturerUrlSrc} onError={() => setManufacturerUrlSource(undefined)} hide={false} />
        ) : (
          manufacturerName && (
            <Typography component="p" variant="body1">
              {manufacturerName}
            </Typography>
          )
        )
      }
      ShortDescription={
        loading ? (
          <Skeleton width="100%" height={63} />
        ) : (
          shortDescription && <Typography variant="h6">{shortDescription}</Typography>
        )
      }
      Code={loading ? <Skeleton width="100%" height={30} /> : code && <ProductCode variant="body1">{code}</ProductCode>}
      DealBadge={dealBadge}
      Price={
        !allowViewPricing || isPriceOnApplication ? null : loading ? (
          <PriceDetailLayout
            PricePerUnit={<Skeleton width={50} height={24} />}
            PricePerWatt={<Skeleton width={50} height={24} />}
          />
        ) : hasSelectedDistributor && !shouldShowSelectedDistributorPricing ? (
          <PriceDetailLayout
            PricePerUnit={<Typography variant="subtitle1">&nbsp;-</Typography>}
            PricePerWatt={<Typography variant="subtitle1">&nbsp;-</Typography>}
          />
        ) : (
          <PriceDetail
            from={!hasSelectedDistributor}
            currencySymbol={currencySymbol}
            pricePerItem={bulkOption?.price_per_unit ?? price}
            discountSavingPerItem={!bulkOption ? discountPercentValue : undefined}
            pricePerWatt={pricePerWatt}
            displayDistributorName={hasSelectedDistributor ? displayDetailsDistributor?.distributor_name : undefined}
          />
        )
      }
    />
  )
}

const ProductCardLayout = ({
  Thumb,
  ManufacturerLogo,
  ShortDescription,
  Code,
  DealBadge,
  Price,
}: {
  Thumb: React.ReactNode
  ManufacturerLogo: React.ReactNode
  ShortDescription: React.ReactNode
  Code: React.ReactNode
  DealBadge?: React.ReactNode
  Price: React.ReactNode
}) => {
  return (
    <Wrapper>
      <Grid container spacing={2} wrap="nowrap" alignItems="flex-start">
        <ImageContainer item>
          <ImageWrapper>{Thumb}</ImageWrapper>
        </ImageContainer>

        <DescriptionContainer item>
          <Grid item>{ManufacturerLogo}</Grid>

          <Grid item>{ShortDescription}</Grid>

          <Grid item>{Code}</Grid>

          {DealBadge && <Grid item>{DealBadge}</Grid>}

          <Grid item style={{ marginTop: 32 }}>
            {Price}
          </Grid>
        </DescriptionContainer>
      </Grid>
    </Wrapper>
  )
}

export default ProductCard
